import type { AppType } from '@praxi/server'
import { hc } from 'hono/client'
import { strings } from './strings'

/** The shape `middleware/error.ts` answers with. */
type ErrorBody = { error: { message: string; errorId: string } }

/**
 * Carries the German message the server produced, so components display the
 * server's wording instead of inventing their own. `errorId` is the link to
 * the log line and is shown only where it helps support.
 */
export class ApiError extends Error {
  readonly status: number
  readonly errorId: string | undefined
  /**
   * True when the request never reached the API. Worth telling apart from a
   * server error: "the server is not running" and "the server refused this"
   * need different reactions, and one message for both sends you looking in
   * the wrong place.
   */
  readonly unreachable: boolean

  constructor(
    status: number,
    message: string,
    options: { errorId?: string | undefined; unreachable?: boolean; cause?: unknown } = {},
  ) {
    super(message, { cause: options.cause })
    this.name = 'ApiError'
    this.status = status
    this.errorId = options.errorId
    this.unreachable = options.unreachable ?? false
  }
}

/** Status 0 where no answer arrived at all — there is no HTTP status to report.
 *  Exported for the upload, which goes through XHR rather than `fetch` (N4). */
export function unreachable(status: number, cause?: unknown): ApiError {
  return new ApiError(status, strings.error.serverUnreachable, { unreachable: true, cause })
}

/**
 * `fetch` rejects only when the request never reached a server: the process is
 * down, or the machine is offline. In production the SPA and the API are one
 * process, so this is exactly what a stopped server looks like there.
 */
const apiFetch: typeof fetch = async (input, init) => {
  try {
    return await fetch(input, init)
  } catch (cause) {
    throw unreachable(0, cause)
  }
}

/**
 * Typed client over the Hono app. The base URL is relative on purpose: in
 * development Vite proxies /api to the server on port 3000, in production the
 * same process serves both. No environment switch in the code.
 */
export const api = hc<AppType>('/', { fetch: apiFetch })

/**
 * Turns a failed response into an `ApiError`.
 *
 * Every error the API itself produces goes through `middleware/error.ts` and
 * carries the envelope above. A reply that is not JSON therefore did not come
 * from the API but from something in between — in development that is Vite's
 * proxy, which answers with a plain-text 500 when the API process is not
 * listening. Reporting that as "an unexpected error occurred" sends you
 * hunting through server logs that do not contain anything.
 */
export async function apiError(response: Response): Promise<ApiError> {
  let body: unknown
  try {
    body = await response.json()
  } catch (cause) {
    // Too large is an answer even from something in between — a proxy in
    // front of the API refuses an upload with a bare 413 — and "not
    // reachable" would send one looking for a stopped server (N2).
    if (response.status === 413) {
      return new ApiError(413, strings.note.fileTooLargeUnknownLimit, { cause })
    }
    return unreachable(response.status, cause)
  }

  const envelope = (body as ErrorBody | null)?.error
  if (envelope?.message) {
    return new ApiError(response.status, envelope.message, { errorId: envelope.errorId })
  }

  // JSON, but not our shape — it did come from somewhere that speaks JSON, so
  // this is not the unreachable case.
  return new ApiError(response.status, strings.error.generic)
}

/** Thrown when an upload was aborted on purpose — cancelled, or the screen it
 *  belonged to was left. Not an error to show. */
export class UploadAbortedError extends Error {
  override readonly name = 'UploadAbortedError'
}

export type UploadProgress = (loaded: number, total: number) => void

/**
 * Sends one file as the raw body of a request and answers with the parsed
 * reply — the transport of every upload: a note's attachment, the files of
 * the Lemniscus import.
 *
 * Through `XMLHttpRequest`, because `fetch` reports no upload progress (N4).
 * Same origin, same cookie; a failed answer goes through `apiError` like every
 * other, so the screen says what the server said — or "zu groß" for a bare 413
 * from a proxy, or "nicht erreichbar" when no answer came at all. Aborting the
 * signal aborts the request.
 */
export function sendFile<Answer>(
  method: 'POST' | 'PUT',
  url: string,
  file: File,
  { onProgress, signal }: { onProgress: UploadProgress; signal: AbortSignal },
): Promise<Answer> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new UploadAbortedError())
      return
    }
    const request = new XMLHttpRequest()
    request.open(method, url)
    request.setRequestHeader('Content-Type', 'application/octet-stream')
    request.setRequestHeader('X-File-Name', encodeURIComponent(file.name))

    request.upload.onprogress = (event) =>
      onProgress(event.loaded, event.lengthComputable ? event.total : file.size)
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        resolve(JSON.parse(request.responseText) as Answer)
        return
      }
      const answer = new Response(request.responseText, {
        status: request.status,
        headers: { 'content-type': request.getResponseHeader('content-type') ?? 'text/plain' },
      })
      void apiError(answer).then(reject)
    }
    request.onerror = () => reject(unreachable(0))
    request.onabort = () => reject(new UploadAbortedError())
    signal.addEventListener('abort', () => request.abort(), { once: true })

    request.send(file)
  })
}
